// The panel's "To fix" list and its count tiles (B8, direction C).
//
// Like the live map's Attention list (live-map.attention.ts), every item
// states a FACT — "has no therapist", "has not clocked in" — and offers the
// one view where the operator can act on it. It never decides a remedy.
//
// In this order: an open SOS, a booking with no therapist, someone offline,
// someone whose shift has started but who has not clocked in, and the first
// hour still ahead where no therapist on duty is free.
//
// ⚠ The design lists only therapists as "not clocked in"; riders are listed
// too, because a rider missing from his shift strands the sessions he drives.

import {
  type CalendarDay,
  type CalendarFilters,
  clockLabel,
  inHub,
  isToday,
  serviceLabel,
  spanLabel,
} from "./calendar-board";
import { type HourCell } from "./calendar-board.hours";

export type FixTarget =
  | { kind: "assign"; bookingId: string }
  | { kind: "person"; staffId: string }
  | { kind: "hour"; startMin: number };

export interface CalendarFixItem {
  id: string;
  tone: "danger" | "warning";
  title: string;
  detail: string;
  /** The button's words: "Assign", "See shift", "Show that hour". */
  action: string;
  target: FixTarget;
}

export interface CalendarTiles {
  needTherapist: number;
  notClockedIn: number;
  sessionsNow: number;
  freeNextHour: number;
}

function notClockedIn(day: CalendarDay, filters: CalendarFilters) {
  return day.rows.filter(
    (row) => inHub(filters, row.hubId) && row.status.kind === "not_clocked_in",
  );
}

export function buildCalendarFixes(
  day: CalendarDay,
  filters: CalendarFilters,
  hubName: (hubId: string) => string,
  strip: readonly HourCell[],
): CalendarFixItem[] {
  const items: CalendarFixItem[] = [];

  // An SOS or a lost signal comes first: someone may need help now. The SOS
  // itself is handled on the live map (features/sos); this only points at it.
  for (const row of day.rows) {
    if (!inHub(filters, row.hubId)) continue;
    if (row.status.kind === "sos") {
      items.push({
        id: `sos:${row.staffId}`,
        tone: "danger",
        title: `${row.name} raised an SOS`,
        detail: `${row.role === "rider" ? "Rider" : "Therapist"} · ${row.hubName} hub`,
        action: "See person",
        target: { kind: "person", staffId: row.staffId },
      });
    } else if (row.status.kind === "disconnected") {
      items.push({
        id: `offline:${row.staffId}`,
        tone: "warning",
        title: `${row.name} is offline`,
        detail: "No signal from their phone",
        action: "See person",
        target: { kind: "person", staffId: row.staffId },
      });
    }
  }

  for (const session of day.unassigned) {
    if (!inHub(filters, session.hubId)) continue;
    const minutes = session.startMin - day.nowMin;
    items.push({
      id: `assign:${session.bookingId}`,
      tone: "danger",
      title: `${serviceLabel(session)} in ${hubName(session.hubId)} has no therapist`,
      detail: `Starts ${clockLabel(session.startMin)}${
        isToday(day) && minutes > 0 && minutes < 60 ? ` · in ${minutes} min` : ""
      }`,
      action: "Assign",
      target: { kind: "assign", bookingId: session.bookingId },
    });
  }

  for (const row of notClockedIn(day, filters)) {
    const first = row.blocks.find((block) => block.startMin > day.nowMin);
    items.push({
      id: `clock:${row.staffId}`,
      tone: "warning",
      title: `${row.name} has not clocked in`,
      detail: [
        `Shift started ${clockLabel(row.shift?.startMin ?? 0)}`,
        first
          ? `first ${row.role === "rider" ? "trip" : "session"} ${clockLabel(first.startMin)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      action: "See shift",
      target: { kind: "person", staffId: row.staffId },
    });
  }

  const tight = strip.find((cell) => !cell.past && cell.free === 0);
  if (tight) {
    items.push({
      id: `hour:${tight.startMin}`,
      tone: "warning",
      title: tight.onDuty > 0 ? "Every therapist on duty is busy" : "No therapist on duty",
      detail: spanLabel({
        startMin: tight.startMin,
        endMin: tight.startMin + 60,
      }),
      action: "Show that hour",
      target: { kind: "hour", startMin: tight.startMin },
    });
  }

  // Most urgent first; within a kind, the order they were found (sort is stable).
  const order = ["sos", "assign", "offline", "clock", "hour"];
  return items.sort((a, b) => order.indexOf(kindOf(a)) - order.indexOf(kindOf(b)));
}

const kindOf = (item: CalendarFixItem) => item.id.slice(0, item.id.indexOf(":"));

export function calendarTiles(day: CalendarDay, filters: CalendarFilters): CalendarTiles {
  const today = isToday(day);
  const soon = day.nowMin + 30;
  const therapists = day.rows.filter(
    (row) => row.role === "therapist" && inHub(filters, row.hubId),
  );
  return {
    needTherapist: day.unassigned.filter((session) => inHub(filters, session.hubId)).length,
    notClockedIn: notClockedIn(day, filters).length,
    sessionsNow: today
      ? day.sessions.filter(
          (session) =>
            session.phase !== "unassigned" &&
            inHub(filters, session.hubId) &&
            session.startMin <= day.nowMin &&
            session.endMin > day.nowMin,
        ).length
      : 0,
    freeNextHour: today
      ? therapists.filter(
          (row) =>
            row.shift !== null &&
            row.shift.startMin <= soon &&
            row.shift.endMin > soon &&
            !row.blocks.some((block) => block.startMin <= soon && block.endMin > soon),
        ).length
      : 0,
  };
}
